import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, MessageCircle, Mail, Phone, MapPin } from "lucide-react";
import { BRAND, PILLARS, GMAIL_COMPOSE } from "@/data/content";

const whatsappUrl = `https://wa.me/${BRAND.whatsapp.replace(/[^0-9]/g, "")}`;

const SOCIALS = [
  { key: "linkedin", href: BRAND.linkedin, Icon: Linkedin, label: "LinkedIn" },
  { key: "instagram", href: BRAND.instagram, Icon: Instagram, label: "Instagram" },
  { key: "facebook", href: BRAND.facebook, Icon: Facebook, label: "Facebook" },
  { key: "whatsapp", href: whatsappUrl, Icon: MessageCircle, label: "WhatsApp" },
  { key: "email", href: GMAIL_COMPOSE, Icon: Mail, label: "Email" },
];

export default function Footer() {
  return (
    <footer data-testid="main-footer" className="border-t border-[#1E3A5F] bg-[#040D17]">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Link to="/" data-testid="footer-logo" className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white p-1 ring-1 ring-gold/40">
                <img src={BRAND.logo} alt={`${BRAND.company} logo`} className="h-full w-full object-contain" />
              </span>
              <span className="flex flex-col">
                <span className="font-serif text-2xl font-semibold text-white">{BRAND.company}</span>
                <span className="text-xs uppercase tracking-[0.25em] text-slate-500">{BRAND.name}</span>
              </span>
            </Link>
            <p className="mt-4 max-w-sm font-serif italic text-lg text-slate-400">{BRAND.tagline}</p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map(({ key, href, Icon, label }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-${key}-link`}
                  aria-label={label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[#1E3A5F] text-slate-400 transition-colors hover:border-gold hover:text-gold"
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="kicker text-gold">Services</h4>
            <ul className="mt-5 space-y-3">
              {PILLARS.map((p) => (
                <li key={p.to}>
                  <Link
                    to={p.to}
                    data-testid={`footer-service-${p.slug}`}
                    className="text-sm text-slate-400 transition-colors hover:text-white"
                  >
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="kicker text-gold">Get in Touch</h4>
            <ul className="mt-5 space-y-3 text-sm text-slate-400">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-gold/70" strokeWidth={1.5} />
                <span data-testid="footer-phone">{BRAND.phone}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-gold/70" strokeWidth={1.5} />
                <span data-testid="footer-email">{BRAND.email}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" strokeWidth={1.5} />
                <span data-testid="footer-address">{BRAND.address}</span>
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <Link to="/about" data-testid="footer-about-link" className="text-slate-400 transition-colors hover:text-white">About</Link>
              <Link to="/testimonials" data-testid="footer-testimonials-link" className="text-slate-400 transition-colors hover:text-white">Testimonials</Link>
              <Link to="/blog" data-testid="footer-blog-link" className="text-slate-400 transition-colors hover:text-white">Blog</Link>
              <Link to="/contact" data-testid="footer-contact-link" className="text-slate-400 transition-colors hover:text-white">Contact</Link>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-[#1E3A5F] pt-8 text-xs text-slate-500 md:flex-row">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {BRAND.company} · {BRAND.name}. All rights reserved.</p>
          <p className="font-serif italic">Old-world credibility. New-world innovation.</p>
          <Link to="/admin" data-testid="footer-admin-link" className="transition-colors hover:text-slate-300">
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
